/**
 * Scan planning for multi-select in the library Finder.
 *
 * Default: walk only each selected folder (scan_path), never the whole NAS tree.
 * Parent collapse is a last resort when per-folder sources cannot be resolved.
 */

import type { ScanSource } from './libraryScanCoordinator';

export function normScanPath(p: string): string {
  return (p || '').replace(/\\/g, '/').replace(/\/+$/, '') || '/';
}

function parentScanPath(p: string): string {
  const n = normScanPath(p);
  const i = n.lastIndexOf('/');
  return i > 0 ? n.slice(0, i) : n;
}

/** Longest shared path prefix (e.g. 800 album folders → /Volumes/share/Rock Music). */
export function longestCommonPathPrefix(paths: string[]): string {
  const ps = paths.map(normScanPath).filter((p) => p && p !== '/');
  if (!ps.length) return '';
  if (ps.length === 1) return ps[0];
  let prefix = ps[0];
  for (let i = 1; i < ps.length; i++) {
    let p = ps[i];
    while (p && !p.startsWith(prefix)) {
      prefix = parentScanPath(prefix);
      if (!prefix || prefix === '/') return '';
    }
  }
  return prefix;
}

/** Parent collapse only when targeted planning fails and selection count is large. */
export const PARENT_COLLAPSE_MIN_SELECTIONS = 40;

/** Collapse when 2+ sibling folders are selected (album grid, Select All, etc.). */
export const MULTI_SELECT_SCAN_THRESHOLD = 2;

export type ScanPlanInput = {
  selectedPaths: string[];
  scanSources?: ScanSource[];
  prebuiltSources?: ScanSource[];
};

/** Pick the registered source whose root contains every selected path. */
export function findEnclosingSource(
  paths: string[],
  sources: Array<{ id?: string; path?: string }>,
): { id: string; path: string } | null {
  let best: { id: string; path: string; len: number } | null = null;
  for (const src of sources) {
    const root = normScanPath(src.path || '');
    const id = src.id;
    if (!root || !id) continue;
    const containsAll = paths.every((p) => {
      const n = normScanPath(p);
      return n === root || n.startsWith(`${root}/`);
    });
    if (!containsAll) continue;
    if (!best || root.length > best.len) {
      best = { id, path: root, len: root.length };
    }
  }
  return best ? { id: best.id, path: best.path } : null;
}

function buildTargetedMultiScan(
  paths: string[],
  registered: Array<{ id?: string; path?: string }>,
  resolveSource?: (folderPath: string) => ScanSource | null,
): ScanPlanInput | null {
  const scanSources: ScanSource[] = [];
  for (const raw of paths) {
    const folder = normScanPath(raw);
    const enclosing = findEnclosingSource([folder], registered);
    if (enclosing) {
      scanSources.push(buildSingleSourceScan(enclosing.id, enclosing.path, folder));
      continue;
    }
    if (resolveSource) {
      const resolved = resolveSource(folder);
      if (resolved?.id) {
        scanSources.push(buildSingleSourceScan(resolved.id, resolved.path, folder));
        continue;
      }
    }
    return null;
  }
  console.log(
    `[Scan] Targeted plan: ${paths.length} folder(s) — scanning each selection, not the full parent tree`,
  );
  return {
    selectedPaths: paths.map(normScanPath),
    scanSources: dedupeTargetedScanSources(scanSources),
  };
}

function buildSingleSourceScan(
  sourceId: string,
  sourceRoot: string,
  scanFolder: string,
): ScanSource {
  const root = normScanPath(sourceRoot);
  const target = normScanPath(scanFolder);
  if (target === root) {
    return { id: sourceId, path: root };
  }
  if (target.startsWith(`${root}/`)) {
    return { id: sourceId, path: root, scan_path: target };
  }
  // Target is outside the registered root — walk only that folder using its own path.
  return { id: sourceId, path: target };
}

/** True when sources already carry per-folder scan_path (targeted multi-select plan). */
export function isTargetedScanPlan(sources: ScanSource[]): boolean {
  if (sources.length < MULTI_SELECT_SCAN_THRESHOLD) return false;
  let withSubfolder = 0;
  for (const s of sources) {
    const sub = s.scan_path ? normScanPath(s.scan_path) : '';
    const root = normScanPath(s.path);
    if (sub && sub !== root) withSubfolder += 1;
  }
  return withSubfolder >= Math.max(2, Math.floor(sources.length * 0.5));
}

/** Drop duplicate walk targets while preserving order. */
export function dedupeTargetedScanSources(sources: ScanSource[]): ScanSource[] {
  const seen = new Set<string>();
  const out: ScanSource[] = [];
  for (const s of sources) {
    if (!s?.id || !s.path) continue;
    const key = `${s.id}::${normScanPath(s.scan_path || s.path)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

/**
 * Build a scan plan for 2+ selected folders. Always prefers targeted per-folder
 * walks; only collapses to a shared parent when per-folder resolution fails.
 */
export function planFastScanForSelections(
  folderPaths: string[],
  options?: {
    registeredSources?: Array<{ id?: string; path?: string }>;
    resolveSource?: (folderPath: string) => ScanSource | null;
  },
): ScanPlanInput | null {
  const paths = folderPaths.map(normScanPath).filter(Boolean);
  if (paths.length < MULTI_SELECT_SCAN_THRESHOLD) return null;

  const common = longestCommonPathPrefix(paths);
  const depth = common.split('/').filter(Boolean).length;
  if (!common || depth < 2) return null;

  const registered = options?.registeredSources || [];

  const targeted = buildTargetedMultiScan(paths, registered, options?.resolveSource);
  if (targeted) return targeted;

  if (paths.length < PARENT_COLLAPSE_MIN_SELECTIONS) return null;

  const enclosing = findEnclosingSource(paths, registered);

  let sourceId = enclosing?.id;
  let sourceRoot = enclosing?.path;

  if (!sourceId && options?.resolveSource) {
    const resolved = options.resolveSource(common);
    if (resolved?.id) {
      sourceId = resolved.id;
      sourceRoot = normScanPath(resolved.path);
    }
  }

  if (sourceId && sourceRoot) {
    const source = buildSingleSourceScan(sourceId, sourceRoot, common);
    const display = source.scan_path || source.path;
    console.log(
      `[Scan] Fast plan: ${paths.length} folder(s) selected → one scan of ${display}`,
    );
    return {
      selectedPaths: [display],
      scanSources: [source],
    };
  }

  console.log(
    `[Scan] Fast plan: ${paths.length} folder(s) selected → one scan of ${common}`,
  );
  return {
    selectedPaths: [common],
  };
}

/** Safety net inside startScan — collapse only large bulk selects to one parent walk. */
export function collapseBulkScanPlan(input: ScanPlanInput): ScanPlanInput {
  const usesScanSources = !!(input.scanSources?.length);
  const sources = usesScanSources ? input.scanSources! : input.prebuiltSources;
  if (!sources?.length || sources.length < MULTI_SELECT_SCAN_THRESHOLD) return input;

  // Finder / planFastScanForSelections already built a targeted plan — do not rebuild.
  if (isTargetedScanPlan(sources)) {
    const deduped = dedupeTargetedScanSources(sources);
    return {
      selectedPaths: deduped.map((s) => normScanPath(s.scan_path || s.path)),
      scanSources: usesScanSources ? deduped : undefined,
      prebuiltSources: usesScanSources ? undefined : deduped,
    };
  }

  const folderPaths = sources.map((s) => normScanPath(s.scan_path || s.path));

  const uniqueById = new Map<string, ScanSource>();
  for (const s of sources) {
    if (s?.id && !uniqueById.has(s.id)) uniqueById.set(s.id, s);
  }
  const registered = [...uniqueById.values()].map((s) => ({ id: s.id, path: s.path }));
  const targeted = buildTargetedMultiScan(folderPaths, registered);
  if (targeted) {
    return {
      selectedPaths: targeted.selectedPaths,
      scanSources: usesScanSources ? targeted.scanSources : undefined,
      prebuiltSources: usesScanSources ? undefined : targeted.scanSources,
    };
  }

  if (sources.length < PARENT_COLLAPSE_MIN_SELECTIONS) return input;

  const common = longestCommonPathPrefix(folderPaths);
  const depth = common.split('/').filter(Boolean).length;
  if (!common || depth < 2) return input;

  let collapsed: ScanSource;
  if (uniqueById.size === 1) {
    const only = uniqueById.values().next().value!;
    collapsed = buildSingleSourceScan(only.id, only.path, common);
  } else {
    const anchor = sources.find((s) => s?.id) || sources[0];
    if (!anchor?.id) return input;
    collapsed = buildSingleSourceScan(anchor.id, common, common);
  }

  const display = collapsed.scan_path || collapsed.path;
  console.log(
    `[Scan] Collapsed ${sources.length} target(s) → one scan of ${display}`,
  );

  return {
    selectedPaths: [display],
    scanSources: usesScanSources ? [collapsed] : undefined,
    prebuiltSources: usesScanSources ? undefined : [collapsed],
  };
}
